"use client";

import { useEffect, useRef, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { MagnifyingGlass } from "@phosphor-icons/react";

type PlazaLiveSearchProps = {
  initialQuery: string;
  onSearch?: (query: string) => void;
  onQueryChange?: () => void;
  busy?: boolean;
  failed?: boolean;
  tags?: string[];
  category?: string;
  sort?: string;
};

export function PlazaLiveSearch({ initialQuery, tags, category, sort, onSearch, onQueryChange, busy, failed }: PlazaLiveSearchProps) {
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<number | undefined>(undefined);
  const composingRef = useRef(false);
  const [query, setQuery] = useState(initialQuery);
  const [pending, startTransition] = useTransition();

  useEffect(() => {
    if (document.activeElement !== inputRef.current) setQuery(initialQuery);
  }, [initialQuery]);

  useEffect(() => {
    const syncFromHistory = () => {
      window.clearTimeout(timerRef.current);
      setQuery(new URLSearchParams(window.location.search).get("q") ?? "");
    };
    window.addEventListener("popstate", syncFromHistory);
    return () => {
      window.clearTimeout(timerRef.current);
      window.removeEventListener("popstate", syncFromHistory);
    };
  }, []);

  function search(value: string) {
    const nextQuery = value.trim().slice(0, 80);
    if (onSearch) {
      if (nextQuery !== initialQuery || failed) onSearch(nextQuery);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    if ((params.get("q") ?? "") === nextQuery && !params.has("page")) return;
    if (nextQuery) params.set("q", nextQuery);
    else params.delete("q");
    params.delete("page");
    const nextParams = params.toString();
    startTransition(() => router.replace(nextParams ? `/plaza?${nextParams}` : "/plaza", { scroll: false }));
  }

  function scheduleSearch(value: string) {
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => search(value), 250);
  }

  return (
    <form method="get" action="/plaza" className="relative" onSubmit={(event) => {
      event.preventDefault();
      if (composingRef.current) return;
      window.clearTimeout(timerRef.current);
      search(query);
    }}>
      {tags?.map(tag => <input type="hidden" name="tag" value={tag} key={tag} />)}
      {category && <input type="hidden" name="category" value={category} />}
      {sort && sort !== "recommend" && <input type="hidden" name="sort" value={sort} />}
      <MagnifyingGlass size={19} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[var(--color-faint)]" aria-hidden="true" />
      <input
        ref={inputRef}
        type="search"
        name="q"
        value={query}
        maxLength={80}
        onChange={(event) => {
          const nextQuery = event.target.value;
          onQueryChange?.();
          setQuery(nextQuery);
          if (!composingRef.current) scheduleSearch(nextQuery);
        }}
        onCompositionStart={() => {
          onQueryChange?.();
          composingRef.current = true;
          window.clearTimeout(timerRef.current);
        }}
        onCompositionEnd={(event) => {
          composingRef.current = false;
          setQuery(event.currentTarget.value);
          scheduleSearch(event.currentTarget.value);
        }}
        placeholder="搜索形象、风格、题材关键词"
        aria-label="搜索形象"
        className="h-12 w-full rounded-full border border-[var(--color-line-strong)] bg-[var(--color-sunken)] pl-12 pr-20 text-sm text-[var(--color-paper)] placeholder:text-[var(--color-faint)] sm:h-14"
      />
      {failed ? <button type="submit" className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-[var(--color-muted)]">重试搜索</button> : (pending || busy || query.trim() !== initialQuery) && <span role="status" className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs text-[var(--color-muted)]">搜索中…</span>}
    </form>
  );
}
